import { Fragment, useState, useEffect } from "react";
import { Card, CardBody, CardHeader, CardTitle, Col, FormGroup, Label, Row, Button, Input } from "reactstrap";
import * as Yup from "yup";
import SelectTipoContratoSophus from "../../../../../components/Select/SelectTipoContratoSophus";
import { optionsPJ as optionsTipoContratoPJ } from "../../../../../components/Select/SelectTipoContratoSophus/options";
import { formatPositiveCurrency } from "../../../../../utils/converts";
import { dateMask } from "../../../../../utils/mask";

const Step2 = ({ values, setValues, setCurrentStep }) => {
  const [tipoContratoValue, setTipoContratoValue] = useState();

    const validationSchema = Yup.object().shape({
    tipo_contrato: Yup.string().required('Tipo Contrato é obrigatório').uppercase().oneOf(["AD", "AG", "AL", "AR", "C1", "C2", "C3", "C4", "C5", "C6", "CA", "CB", "CC", "CH", "CI", "CO", "CP", "CR", "CS", "CT", "DC", "DB", "DE", "DP", "EC", "EE"], "Tipo Contrato inválido"),
    contrato: Yup.string().required("Contrato é obrigatório"),
    data_vencimento: Yup.string()
      .required("Data de Vencimento é obrigatória")
      .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato de data inválido (DD/MM/YYYY)')
      .test('isValidDate', 'Data inválida', function (value) {
        if (!value) return true;
        const [day, month, year] = value.split('/').map(Number);
        const dateObject = new Date(year, month - 1, day);

        return (
          dateObject.getFullYear() === year &&
          dateObject.getMonth() === month - 1 &&
          dateObject.getDate() === day
        );
      })
      .test('isMaximumAge', 'Data de Vencimento precisa ter no máximo 5 anos', function (value) {
        if (!value) return true;
        const [day, month, year] = value.split('/').map(Number);
        const today = new Date();
        const maximumAgeDate = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate());
        const inputDate = new Date(year, month - 1, day);
        return inputDate >= maximumAgeDate;
      })
      .test('hasOneDayDifference', 'Data de Vencimento precisa ser menor que a data de hoje', function (value) {
        if (!value) return true;
        const [day, month, year] = value.split('/').map(Number);
        const inputDate = new Date(year, month - 1, day);
        const today = new Date();
        today.setDate(today.getDate() - 1);
        return inputDate <= today;
      }),
    valor: Yup.string().required("Valor é obrigatório")
      .test('valueGreaterThanZero', 'Valor precisa ser maior que 0,00', function (value) {
        if (!value) return true;

        return value !== "0,00";
      })
  });

  const validate = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });

      setErrors({});
      setValues(formData);
      setCurrentStep(2);
      return true;
    } catch (error) {
      const validationErrors = {};
      error.inner.forEach((err) => {
        validationErrors[err.path] = err.message;
      });

      setErrors(validationErrors);

      return false;
    }
  };

  const handleSetTipoContratoValue = (value) => {
    const _tipoContratoValue = optionsTipoContratoPJ.find(opt => opt.value === value);
    setTipoContratoValue(_tipoContratoValue);
  }

  const handlePrevious = () => {
    setValues(formData);
    setCurrentStep(0);
  }

  useEffect(() => {
    if (values.tipo_contrato) {
      handleSetTipoContratoValue(values.tipo_contrato);
    }

    setFormData({
      tipo_contrato: values.tipo_contrato,
      contrato: values.contrato,
      data_vencimento: values.data_vencimento,
      valor: values.valor,
      observacao: values.observacao
    });
  }, [values])

  const [formData, setFormData] = useState({
    tipo_contrato: '',
    contrato: '',
    data_vencimento: '',
    valor: '',
    observacao: '',
  });

  const [errors, setErrors] = useState({
    tipo_contrato: '',
    contrato: '',
    data_vencimento: '',
    valor: '',
    observacao: ''
  });

  const setFieldValue = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  return (
    <Fragment>
      <Card className="border-custom">
        <CardHeader className="card-custom">
          <CardTitle className="card-text-custom">Dados da Dívida</CardTitle>
        </CardHeader>
        <CardBody>
          <Row>
            <Col sm="6" md="4">
              <FormGroup>
                <Label htmlFor="tipo_contrato"><span className="text-danger">***</span> Tipo Contrato</Label>
                <SelectTipoContratoSophus
                  name="tipo_contrato"
                  id="tipo_contrato"
                  placeholder="Selecione o Tipo de Contrato"
                  className={errors.tipo_contrato ? " is-invalid invalid-multi-select" : ""}
                  onChange={(data) => { setFieldValue("tipo_contrato", data ? data.value : ""); handleSetTipoContratoValue(data.value) }}
                  isSearchable={true}
                  isClearable={true}
                  value={tipoContratoValue}
                  typeOptions={"PJ"}
                />
                <div className="invalid-feedback">{errors.tipo_contrato}</div>
              </FormGroup>
            </Col>

            <Col sm="6" md="4">
              <FormGroup>
                <Label htmlFor="contrato"><span className="text-danger">***</span> Contrato</Label>
                <Input
                  type="text"
                  name="contrato"
                  id="contrato"
                  placeholder="Digite o Contrato"
                  className={"form-control" + (errors.contrato ? " is-invalid" : "")}
                  onChange={(e) => setFieldValue('contrato', e.target.value)}
                  value={formData.contrato}
                />
                <div className="invalid-feedback">{errors.contrato}</div>
              </FormGroup>
            </Col>

            <Col sm="6" md="4">
              <FormGroup>
              <Label htmlFor="valor"><span className="text-danger">***</span> Valor R$</Label>
                <Input
                  type="text"
                  name="valor"
                  id="valor"
                  placeholder="Digite o Valor"
                  className={"form-control" + (errors.valor ? " is-invalid" : "")}
                  onChange={(e) => setFieldValue('valor', formatPositiveCurrency(e.target.value))}
                  value={formData.valor}
                />
                <div className="invalid-feedback">{errors.valor}</div>
              </FormGroup>
            </Col>

            <Col sm="6" md="4">
              <FormGroup>
                <Label htmlFor="data_vencimento"><span className="text-danger">***</span> Data de Vencimento</Label>
                <Input
                  type="text"
                  name="data_vencimento"
                  id="data_vencimento"
                  placeholder="Digite a Data de Vencimento"
                  className={"form-control" + (errors.data_vencimento ? " is-invalid" : "")}
                  onChange={(e) => setFieldValue('data_vencimento', dateMask(e.target.value))}
                  value={formData.data_vencimento}
                  maxLength="10"
                />
                <div className="invalid-feedback">{errors.data_vencimento}</div>
              </FormGroup>
            </Col>

            <Col sm="6" md="4">
              <FormGroup>
                <Label htmlFor="observacao">Observação</Label>
                <Input
                  type="text"
                  name="observacao"
                  id="observacao"
                  placeholder="Digite o Observação"
                  className={"form-control" + (errors.observacao ? " is-invalid" : "")}
                  onChange={(e) => setFieldValue('observacao', e.target.value)}
                  value={formData.observacao}
                />
                <div className="invalid-feedback">{errors.observacao}</div>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>
      <div className="text-center">
        <Button className="mr-1" color="primary" onClick={() => handlePrevious()}>Anterior</Button>
        <Button className="mr-1" color="primary" onClick={validate}>Próximo</Button>
      </div>
    </Fragment>
  );
};

export default Step2;